/* eslint-disable no-nested-ternary */
import React, {useEffect, useMemo, useRef} from 'react'
import useLanguage from 'features/languageReport/useLanguage'
import ReportSubtitle from 'components/Text/ReportSubtitle'
import useStyles from 'components/molcules/N1SummaryReport/useStyles'
import {ResEEGMCI8Stages} from 'api/languageApi'
import {useTranslation} from 'react-i18next'
import AIDementiaPredictionGuide from 'pages/HealthCenter/ReportV2/AIDementiaPrediction/AIDementiaPredictionGuide'

interface MciProps {
  MciImagePath: string
  probability: number
  clientFirstName: string
  clientLastName: string
  textMciAPI: ResEEGMCI8Stages
  footer: string
}

export default function MCIReportPageStage8API({
  MciImagePath,
  probability,
  clientFirstName,
  clientLastName,
  textMciAPI,
  footer,
}: MciProps) {
  const classes = useStyles()
  const {t} = useTranslation()
  const refs = useRef<HTMLDivElement[]>([])
  const clientFullName = (firstName: string, lastName: string): string => {
    const name = t('IGetFullName', {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
    }).trim()
    return name
  }

  const treatmentRef = useRef<HTMLDivElement>(null)

  const data = useMemo(() => {
    const data = textMciAPI?.mci.section.recommendation_table
    if (data)
      return [
        {
          index: 1,
          stepText: `${data.stage1.stage.number} (~40%)`,
          title: data.stage1.stage.description,
          content: data.stage1.recommendations,
          treatment: data.stage1.treatment,
          color: '#43a47b',
          isActive: probability < 40,
        },
        {
          index: 2,
          stepText: `${data.stage2.stage.number} (40~50%)`,
          title: data.stage2.stage.description,
          content: data.stage2.recommendations,
          treatment: data.stage2.treatment,
          color: '#68ae44',
          isActive: probability >= 40 && probability < 50,
        },
        {
          index: 3,
          stepText: `${data.stage3.stage.number} (50~60%)`,
          title: data.stage3.stage.description,
          content: data.stage3.recommendations,
          treatment: data.stage3.treatment,
          color: '#9caf27',
          isActive: probability >= 50 && probability < 60,
        },
        {
          index: 4,
          stepText: `${data.stage4.stage.number} (60~70%)`,
          title: data.stage4.stage.description,
          content: data.stage4.recommendations,
          treatment: data.stage4.treatment,
          color: '#ceb215',
          isActive: probability >= 60 && probability < 70,
        },
        {
          index: 5,
          stepText: `${data.stage5.stage.number} (70~80%)`,
          title: data.stage5.stage.description,
          content: data.stage5.recommendations,
          treatment: data.stage5.treatment,
          color: '#c89331',
          isActive: probability >= 70 && probability < 80,
        },
        {
          index: 6,
          stepText: `${data.stage6.stage.number} (80~85%)`,
          title: data.stage6.stage.description,
          content: data.stage6.recommendations,
          treatment: data.stage6.treatment,
          color: '#be6b29',
          isActive: probability >= 80 && probability < 85,
        },
        {
          index: 7,
          stepText: `${data.stage7.stage.number} (85~90%)`,
          title: data.stage7.stage.description,
          content: data.stage7.recommendations,
          treatment: data.stage7.treatment,
          color: '#b64629',
          isActive: probability >= 85 && probability < 90,
        },
        {
          index: 8,
          stepText: `${data.stage8.stage.number} (90%~)`,
          title: data.stage8.stage.description,
          content: data.stage8.recommendations,
          treatment: data.stage8.treatment,
          color: '#c7302a',
          isActive: probability >= 90,
        },
      ]
    return []
  }, [textMciAPI, probability])

  const formatText = (template: string, values: (string | number)[]) => {
    return template.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }

  const currentState = useMemo(() => {
    const activeStage = data.find((item) => item.isActive)
    if (activeStage)
      return textMciAPI?.mci.section.recommendation_table[
        `stage${activeStage.index}`
      ]
    return {
      recommendations: {
        main: [],
        sub: [],
      },
      treatment: '',
    }
  }, [textMciAPI])

  useEffect(() => {
    if (treatmentRef.current) {
      treatmentRef.current.innerHTML = currentState.treatment || ''
    }
  }, [currentState.treatment])

  return (
    <div className={classes.mci}>
      <br />
      <ReportSubtitle className='ReportTitle' number='1'>
        {textMciAPI?.mci.section.prediction.title ?? ''}
      </ReportSubtitle>
      <img
        src={MciImagePath || ''}
        alt='mci report'
        style={{
          width: '100%',
          height: 'auto',
        }}
      />
      <div className={classes.mciResultDesContent}>
        <div className={classes.mciResultDesContentList}>
          <div className={classes.mciResultDesContentText}>
            <span>1</span>
            <div style={{width: '99%'}}>
              {textMciAPI?.mci.section.prediction.opinions.text_box.circle1}
            </div>
          </div>
          <div className={classes.mciResultDesContentText}>
            <span>2</span>
            <div style={{width: '99%'}}>
              {textMciAPI?.mci.section.prediction.opinions.text_box.circle2}
            </div>
          </div>
        </div>
        <div className={classes.mciResultDesContentText}>
          {textMciAPI?.mci.section.prediction.opinions.text_box.explanation}
        </div>
      </div>
      <div className={classes.mciResultDes}>
        <div className={classes.mciResultDesTitle}>
          {formatText(textMciAPI.mci.section.prediction.opinions.diagnosis, [
            clientFullName(clientFirstName, clientLastName),
            data.find((item) => item.isActive)?.index || 1,
          ])}
        </div>
        <div className={classes.stageRecommendation}>
          {data.map((stage) => (
            <div className={classes.stageWrapper} key={stage.index}>
              <div className={classes.stageTitle}>
                <div
                  style={{
                    color: stage.color,
                  }}
                >
                  {stage.stepText}
                </div>
                <div
                  style={{
                    color: stage.isActive ? stage.color : '#d8d9e0',
                  }}
                >
                  {stage.title.replaceAll('<br>', ' ')}
                </div>
              </div>
              {stage.isActive && (
                <div className={classes.stageMainRecommendation}>
                  <ul
                    style={{
                      listStyleType: 'disc',
                      marginLeft: 20,
                    }}
                  >
                    {stage.content.main?.map((text: string) => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                  {stage.content.sub?.map((text: string) => (
                    <div className={classes.subRecommendation} key={text}>
                      {`‒ ${text}`}
                    </div>
                  ))}
                  <div
                    ref={treatmentRef}
                    style={{
                      marginLeft: 20,
                      marginTop: 10,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ReportSubtitle
        className='ReportTitle'
        style={{
          padding: '10px 5px 10px 10px',
        }}
        number='2'
      >
        {textMciAPI?.mci.section.recommendation_table.title}
      </ReportSubtitle>
      <div className={classes.AIDementiaPredictionDetail}>
        {data.map((content) => (
          <AIDementiaPredictionGuide
            key={content.index}
            stepText={content.stepText}
            color={content.color}
            title={content.title}
            content={content.content}
            isActive={content.isActive}
            treatment={content.treatment}
          />
        ))}
      </div>
      <div className={classes.mciResultNote}>
        {textMciAPI?.mci.section.recommendation_table.annotation.map(
          (_, index) => (
            <div
              className={classes.mciResultNoteElm}
              key={index}
              ref={(el) => {
                if (el) refs.current[index] = el
              }}
            />
          ),
        )}
      </div>
      <div className={classes.footer}>{footer}</div>
    </div>
  )
}
